import { useState, useEffect, useRef } from "react";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { useAuth } from "../../../../context/AuthContext";
import { Building, MapPin, User, Filter, Search, X } from "lucide-react";
import Swal from "sweetalert2";
import {
  obtenerTorres,
  Torre,
} from "../../../../api/torreService";
import {
  obtenerApartamentos,
  obtenerApartamentosConPropietarios,
  bloquearParaVotar,
  Apartamento,
  ApartamentoConPropietario,
} from "../../../../api/apartamentoService";
import EnviarLlaveModal from "./EnviarLlaveModal";
import VerPropietarioModal from "./VerPropietarioModal";

export default function ApartamentosPage() {
  const { session } = useAuth();
  
  const isLoadingCatalogosRef = useRef(false);
  const isLoadingApartamentosRef = useRef(false);
  const catalogosLoadedRef = useRef(false);
  
  const [apartamentos, setApartamentos] = useState<ApartamentoConPropietario[]>([]);
  const [filteredApartamentos, setFilteredApartamentos] = useState<ApartamentoConPropietario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTorre, setSelectedTorre] = useState("");
  const [selectedApartamentoFiltro, setSelectedApartamentoFiltro] = useState("");
  const [torres, setTorres] = useState<Torre[]>([]);
  const [apartamentosFiltro, setApartamentosFiltro] = useState<Apartamento[]>([]);
  const [selectedApartamento, setSelectedApartamento] = useState<ApartamentoConPropietario | null>(null);
  const [showEnviarLlaveModal, setShowEnviarLlaveModal] = useState(false);
  const [showVerPropietarioModal, setShowVerPropietarioModal] = useState(false);

  useEffect(() => {
    const unidadId = session?.unidad?.id;
    
    if (unidadId && !catalogosLoadedRef.current && !isLoadingCatalogosRef.current) {
      catalogosLoadedRef.current = true;
      loadCatalogos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.unidad?.id]);

  useEffect(() => {
    const unidadId = session?.unidad?.id;
    
    if (unidadId) {
      loadApartamentosConPropietarios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.unidad?.id, selectedTorre, selectedApartamentoFiltro]);

  useEffect(() => {
    filterApartamentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, apartamentos]);

  const loadCatalogos = async () => {
    if (isLoadingCatalogosRef.current) return;

    try {
      isLoadingCatalogosRef.current = true;
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) return;

      const torresResponse = await obtenerTorres({
        "unidad residencial codigo": unidadCodigo,
      });

      if (torresResponse.code === "1" && torresResponse.object_) {
        setTorres(torresResponse.object_);
      }
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los catálogos",
        confirmButtonColor: "#3d5a40",
      });
    } finally {
      isLoadingCatalogosRef.current = false;
    }
  };

  const loadApartamentosFiltro = async (torreCodigo: string) => {
    if (!torreCodigo || torreCodigo.trim() === "") {
      setApartamentosFiltro([]);
      return;
    }

    try {
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) {
        setApartamentosFiltro([]);
        return;
      }

      const response = await obtenerApartamentos({
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": torreCodigo,
      });

      if (response.code === "1" && response.object_) {
        setApartamentosFiltro(response.object_);
      } else {
        setApartamentosFiltro([]);
      }
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      setApartamentosFiltro([]);
    }
  };

  const loadApartamentosConPropietarios = async () => {
    if (isLoadingApartamentosRef.current) return;

    try {
      isLoadingApartamentosRef.current = true;
      setIsLoading(true);
      
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) {
        setApartamentos([]);
        setFilteredApartamentos([]);
        return;
      }

      const payload = {
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": selectedTorre || "",
        "apartamento codigo": selectedApartamentoFiltro || "",
      };

      const response = await obtenerApartamentosConPropietarios(payload);

      if (response.code === "1" && response.object_) {
        const sorted = response.object_.sort((a, b) => {
          const torreA = (a.apt_torre_nombre || "").toLowerCase();
          const torreB = (b.apt_torre_nombre || "").toLowerCase();
          if (torreA !== torreB) {
            return torreA.localeCompare(torreB);
          }
          const aptA = (a.apt_nombre || "").toLowerCase();
          const aptB = (b.apt_nombre || "").toLowerCase();
          return aptA.localeCompare(aptB);
        });
        setApartamentos(sorted);
        setFilteredApartamentos(sorted);
      } else {
        setApartamentos([]);
        setFilteredApartamentos([]);
      }
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      const errorMessage = error instanceof Error ? error.message : "No se pudieron cargar los apartamentos";
      
      Swal.fire({
        icon: "error",
        title: "Error al cargar apartamentos",
        text: errorMessage,
        confirmButtonColor: "#3d5a40",
      });
      
      setApartamentos([]);
      setFilteredApartamentos([]);
    } finally {
      isLoadingApartamentosRef.current = false;
      setIsLoading(false);
    }
  };

  const filterApartamentos = () => {
    if (!searchTerm.trim()) {
      setFilteredApartamentos(apartamentos);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = apartamentos.filter(
      (apt) =>
        (apt.apt_nombre || "").toLowerCase().includes(term) ||
        (apt.apt_torre_nombre || "").toLowerCase().includes(term) ||
        (apt.apt_usuario_propietario || "").toLowerCase().includes(term) ||
        (apt.apt_unidad_residencial_nombre || "").toLowerCase().includes(term)
    );
    setFilteredApartamentos(filtered);
  };

  const handleTorreChange = (value: string) => {
    setSelectedTorre(value);
    setSelectedApartamentoFiltro("");
    setApartamentosFiltro([]);
    
    if (value) {
      loadApartamentosFiltro(value);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedTorre("");
    setSelectedApartamentoFiltro("");
    setApartamentosFiltro([]);
  };

  const handleApartamentoClick = (apt: ApartamentoConPropietario) => {
    setSelectedApartamento(apt);
    if (!apt.apt_usuario_propietario) {
      setShowEnviarLlaveModal(true);
    } else {
      setShowVerPropietarioModal(true);
    }
  };

  const handleCloseEnviarLlave = () => {
    setShowEnviarLlaveModal(false);
    setSelectedApartamento(null);
  };

  const handleCloseVerPropietario = () => {
    setShowVerPropietarioModal(false);
    setSelectedApartamento(null);
  };

  const handleBloquearChange = async (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, aptInfo: ApartamentoConPropietario, nuevoValor: number) => {
    e.stopPropagation();
    try {
      // Optistic UI update
      setApartamentos(prev => prev.map(a => 
        a.apt_codigo === aptInfo.apt_codigo 
          ? { ...a, apt_bloqueado_para_votar: nuevoValor } 
          : a
      ));

      const payload = {
        apartamento_codigo: aptInfo.apt_codigo,
        bloqueado_para_votar: nuevoValor,
        razon_del_bloqueo: ""
      };

      const resp = await bloquearParaVotar(payload);
      
      if (resp.code !== "1") {
        throw new Error(resp.message || "Error al actualizar bloqueo");
      }
      
      Swal.fire({
        icon: "success",
        title: "Actualizado",
        text: `El apartamento ha sido ${nuevoValor === 1 ? 'bloqueado' : 'desbloqueado'} exitosamente.`,
        timer: 1500,
        showConfirmButton: false
      });
      
    } catch (error: any) {
      // Revert optimistic update
      setApartamentos(prev => prev.map(a => 
        a.apt_codigo === aptInfo.apt_codigo 
          ? { ...a, apt_bloqueado_para_votar: aptInfo.apt_bloqueado_para_votar } 
          : a
      ));
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Hubo un problema al actualizar la configuración.",
        confirmButtonColor: "#3d5a40",
      });
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300">
      <Sidebar />

      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-military-600 flex items-center gap-2 sm:gap-3">
                <Building size={24} className="sm:w-8 sm:h-8" />
                Gestión de Apartamentos
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Consulta los apartamentos y sus propietarios
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 sm:mt-6">
            <Card className="border-l-4 border-military-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Apartamentos</p>
                    <p className="text-2xl font-bold text-gray-900">{apartamentos.length}</p>
                  </div>
                  <div className="bg-military-100 p-3 rounded-full">
                    <Building size={24} className="text-military-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-blue-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Con Propietario</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {apartamentos.filter((apt) => apt.apt_usuario_propietario).length}
                    </p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full">
                    <User size={24} className="text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-green-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Torres</p>
                    <p className="text-2xl font-bold text-green-600">
                      {torres.length}
                    </p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <Building size={24} className="text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="mb-6 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Filter size={20} />
                Filtros
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-600"
                >
                  <X size={16} className="mr-1" />
                  Limpiar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-gray-600"
                >
                  {showFilters ? "Ocultar" : "Mostrar"}
                </Button>
              </div>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Search size={16} className="inline mr-1" />
                    Buscar
                  </label>
                  <Input
                    placeholder="Apartamento, torre, email propietario..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building size={16} className="inline mr-1" />
                    Torre
                  </label>
                  <select
                    value={selectedTorre}
                    onChange={(e) => handleTorreChange(e.target.value)}
                    className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                  >
                    <option value="">Todas las torres</option>
                    {torres.map((torre) => (
                      <option key={torre.tor_codigo} value={torre.tor_codigo}>
                        {torre.tor_nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin size={16} className="inline mr-1" />
                    Apartamento
                  </label>
                  <select
                    value={selectedApartamentoFiltro}
                    onChange={(e) => setSelectedApartamentoFiltro(e.target.value)}
                    disabled={!selectedTorre}
                    className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Todos los apartamentos</option>
                    {apartamentosFiltro.map((apt) => (
                      <option key={apt.apt_codigo} value={apt.apt_codigo}>
                        {apt.apt_nombre}
                      </option>
                    ))}
                  </select>
                  {!selectedTorre && (
                    <p className="text-xs text-gray-500 mt-1">
                      Selecciona una torre primero
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Apartamentos ({filteredApartamentos.length})
            </h3>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
                <p className="text-gray-600">Cargando apartamentos...</p>
              </div>
            ) : filteredApartamentos.length === 0 ? (
              <div className="text-center py-12">
                <Building size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No se encontraron apartamentos</p>
                <p className="text-sm text-gray-500 mt-2">
                  {searchTerm || selectedTorre || selectedApartamentoFiltro
                    ? "Intenta ajustar los filtros"
                    : "No hay apartamentos disponibles"}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {filteredApartamentos.map((apt, index) => (
                  <div
                    key={`${apt.apt_codigo}-${index}`}
                    onClick={() => handleApartamentoClick(apt)}
                    className={`bg-white rounded-xl p-4 sm:p-5 shadow-sm border ${
                      apt.apt_bloqueado_para_votar === 1 
                        ? 'border-red-400' 
                        : 'border-gray-200 hover:border-military-300'
                    } transition-colors cursor-pointer`}
                  >
                    <div className="flex flex-col h-full">
                      <h3 className="text-lg font-bold text-gray-800 mb-2">
                        {apt.apt_torre_nombre ? `Torre ${apt.apt_torre_nombre}` : "Torre"} apartamento {apt.apt_nombre}
                      </h3>
                      
                      <div className="text-sm text-gray-600 mb-4 flex-grow">
                        <p className="font-semibold text-gray-700">Propietario</p>
                        {apt.apt_usuario_propietario ? (
                          <>
                            <p className="text-gray-800 mt-1">{apt.apt_usuario_propietario_nombre || apt.apt_usuario_propietario.split('@')[0]}</p>
                            <p className="text-gray-500">{apt.apt_usuario_propietario}</p>
                          </>
                        ) : (
                          <p className="text-gray-400 italic mt-1">No asignado</p>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                        <span className="text-sm font-bold text-gray-800">
                          Bloqueado para votar
                        </span>
                        
                        <div className="flex items-center space-x-4">
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="radio"
                              className="form-radio h-5 w-5 text-blue-600 border-gray-300 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                              name={`bloqueo-${apt.apt_codigo}`}
                              value="0"
                              checked={window.parseInt(apt.apt_bloqueado_para_votar?.toString() || "0") === 0}
                              onChange={(e) => handleBloquearChange(e, apt, 0)}
                            />
                            <span className="ml-2 text-sm text-gray-700 font-medium">No</span>
                          </label>
                          
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="radio"
                              className="form-radio h-5 w-5 text-blue-600 border-gray-300 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                              name={`bloqueo-${apt.apt_codigo}`}
                              value="1"
                              checked={window.parseInt(apt.apt_bloqueado_para_votar?.toString() || "0") === 1}
                              onChange={(e) => handleBloquearChange(e, apt, 1)}
                            />
                            <span className="ml-2 text-sm text-gray-700 font-medium">Si</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {showEnviarLlaveModal && selectedApartamento && (
        <EnviarLlaveModal
          isOpen={showEnviarLlaveModal}
          apartamento={selectedApartamento}
          onClose={handleCloseEnviarLlave}
        />
      )}

      {showVerPropietarioModal && selectedApartamento && (
        <VerPropietarioModal
          isOpen={showVerPropietarioModal}
          apartamento={selectedApartamento}
          onClose={handleCloseVerPropietario}
        />
      )}
    </div>
  );
}

